import { StatCard } from '@/components/ui/stat-card';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, Users, DollarSign, Bot } from 'lucide-react';
import { useVendorRanking, useAreaBreakdown } from '@/hooks/useSubscriptions';
import type { GlobalSubscriptionFilters } from '../../../../shared/types';
import { WinclapLoader } from '@/components/ui/winclap-loader';

const fmt = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const fmtCPU = (amount: number, users: number) =>
  users > 0 ? fmt(amount / users) : '—';


export function HeadcountAnalysisTab({ globalFilters = {} }: { globalFilters?: GlobalSubscriptionFilters }) {
  const { t } = useTranslation();
  const { data: vendors = [], isLoading: loadingVendors } = useVendorRanking();
  const { data: areaRows = [], isLoading: loadingArea } = useAreaBreakdown({
    dateFrom: globalFilters.dateFrom,
    dateTo: globalFilters.dateTo,
    department: globalFilters.department,
    vendor: globalFilters.vendor,
  });

  const isLoading = loadingVendors || loadingArea;

  const vendorStats = useMemo(() => {
    return vendors
      .filter((v) => v.Users > 0)
      .map((v) => ({
        ...v,
        costPerUser: v.Total_Amount / v.Users,
      }))
      .sort((a, b) => b.costPerUser - a.costPerUser);
  }, [vendors]);

  const aiVendors = vendorStats.filter((v) => v.Is_AI_Tool);

  // Proxy de usuarios únicos: el máximo de Users entre vendors (no hay emails por fila).
  const totalUsers = useMemo(() => {
    return vendors.reduce((max, v) => Math.max(max, v.Users), 0);
  }, [vendors]);

  const aiUserCoverage = useMemo(() => {
    const maxAIUsers = aiVendors.reduce((max, v) => Math.max(max, v.Users), 0);
    return totalUsers > 0 ? (maxAIUsers / totalUsers) * 100 : 0;
  }, [aiVendors, totalUsers]);

  const avgCostPerUser = useMemo(() => {
    const totalAISpend = aiVendors.reduce((s, v) => s + v.Total_Amount, 0);
    const totalAIUsers = aiVendors.reduce((s, v) => s + v.Users, 0);
    return totalAIUsers > 0 ? totalAISpend / totalAIUsers : 0;
  }, [aiVendors]);

  // Area-level user data
  const areaUserData = useMemo(() => {
    const map = new Map<string, { area: string; total: number; ai: number; users: number }>();
    for (const r of areaRows) {
      const key = r.NetSuite_Departamentos || 'Sin departamento';
      if (!map.has(key)) map.set(key, { area: key, total: 0, ai: 0, users: 0 });
      const s = map.get(key)!;
      s.total += r.Total_Amount;
      s.ai += r.Is_AI_Tool ? r.Total_Amount : 0;
      s.users = Math.max(s.users, r.Users);
    }
    return Array.from(map.values())
      .filter((a) => a.users > 0)
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  }, [areaRows]);

  // Cost per user chart data (top 10 by CPU)
  const cpuChartData = vendorStats.slice(0, 12).map((v) => ({
    vendor: v.Normalized_Vendor.length > 18 ? v.Normalized_Vendor.slice(0, 16) + '…' : v.Normalized_Vendor,
    fullVendor: v.Normalized_Vendor,
    'Cost/User': v.costPerUser,
    isAI: v.Is_AI_Tool,
  }));

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <WinclapLoader size={48} className="text-foreground/25" />
      </div>
    );
  }

  return (
    <div className="space-y-6 mt-4">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard
          icon={Users} label={t('subscriptions.headcount.kpi_max_users')} value={String(totalUsers)}
          sub={t('subscriptions.headcount.kpi_max_users_sub')} iconClassName="text-blue-500"
        />
        <StatCard
          icon={Bot} label={t('subscriptions.headcount.kpi_ai_coverage')}
          value={`${aiUserCoverage.toFixed(0)}%`}
          sub={t('subscriptions.headcount.kpi_ai_coverage_sub')} iconClassName="text-purple-500"
        />
        <StatCard
          icon={DollarSign} label={t('subscriptions.headcount.kpi_avg_cost')}
          value={fmt(avgCostPerUser)}
          sub={t('subscriptions.headcount.kpi_avg_cost_sub')} iconClassName="text-violet-500"
        />
        <StatCard
          icon={TrendingUp} label={t('subscriptions.headcount.kpi_ai_vendors')}
          value={String(aiVendors.length)}
          sub={t('subscriptions.headcount.kpi_ai_vendors_sub')} iconClassName="text-indigo-500"
        />
      </div>

      {/* Cost per user bar chart */}
      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-1">{t('subscriptions.headcount.chart_cpu_title')}</h3>
        <p className="text-xs text-muted-foreground mb-4">{t('subscriptions.headcount.chart_cpu_sub')}</p>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={cpuChartData} layout="vertical" margin={{ top: 0, right: 20, left: 120, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
            <XAxis type="number" tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`} tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="vendor" tick={{ fontSize: 11 }} width={120} />
            <Tooltip
              formatter={(v) => [fmt(Number(v)), 'Cost/User']}
              labelFormatter={(label) => cpuChartData.find((d) => d.vendor === label)?.fullVendor ?? label}
            />
            <Bar dataKey="Cost/User" radius={[0, 3, 3, 0]}>
              {cpuChartData.map((entry, i) => (
                <Cell key={i} fill={entry.isAI ? '#8b5cf6' : '#3b82f6'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <div className="flex gap-4 mt-2 text-xs text-muted-foreground justify-end">
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-purple-500" /> {t('subscriptions.headcount.legend_ai')}</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-blue-500" /> {t('subscriptions.headcount.legend_non_ai')}</span>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Spend per user by area */}
        <Card className="p-5">
          <h3 className="text-sm font-semibold mb-4">{t('subscriptions.headcount.chart_dept_title')}</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={areaUserData.map((a) => ({
                area: a.area.length > 16 ? a.area.slice(0, 14) + '…' : a.area,
                fullArea: a.area,
                'Total/User': a.users > 0 ? a.total / a.users : 0,
                'AI/User': a.users > 0 ? a.ai / a.users : 0,
              }))}
              layout="vertical"
              margin={{ top: 0, right: 10, left: 90, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
              <XAxis type="number" tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`} tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="area" tick={{ fontSize: 10 }} width={90} />
              <Tooltip formatter={(v) => [fmt(Number(v))]} />
              <Bar dataKey="Total/User" fill="#3b82f6" />
              <Bar dataKey="AI/User" fill="#8b5cf6" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* AI vendor adoption table */}
        <Card className="p-5">
          <h3 className="text-sm font-semibold mb-3">{t('subscriptions.headcount.table_adoption_title')}</h3>
          <div className="overflow-y-auto max-h-64">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-card">
                <tr className="border-b text-muted-foreground uppercase tracking-wide">
                  <th className="text-left py-1.5 font-medium">{t('subscriptions.headcount.col_vendor')}</th>
                  <th className="text-right py-1.5 font-medium">{t('subscriptions.headcount.col_users')}</th>
                  <th className="text-right py-1.5 font-medium">{t('subscriptions.headcount.col_total')}</th>
                  <th className="text-right py-1.5 font-medium">{t('subscriptions.headcount.col_cpu')}</th>
                </tr>
              </thead>
              <tbody>
                {aiVendors.map((v) => (
                  <tr key={v.Normalized_Vendor} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="py-1.5 font-medium">
                      <div className="flex items-center gap-1">
                        {v.Normalized_Vendor}
                        {v.Is_AI_Native_Related && (
                          <Badge variant="secondary" className="text-xs bg-indigo-100 text-indigo-700 shrink-0">Native</Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-1.5 text-right">{v.Users}</td>
                    <td className="py-1.5 text-right font-semibold">{fmt(v.Total_Amount)}</td>
                    <td className="py-1.5 text-right text-purple-600 font-medium">{fmtCPU(v.Total_Amount, v.Users)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* All vendors cost/user table */}
      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-3">{t('subscriptions.headcount.table_ranking_title')}</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr className="border-b text-muted-foreground text-xs uppercase tracking-wide">
                <th className="text-left py-2.5 px-4 font-medium">#</th>
                <th className="text-left py-2.5 px-4 font-medium">{t('subscriptions.headcount.col_vendor')}</th>
                <th className="text-left py-2.5 px-4 font-medium">{t('subscriptions.headcount.col_category')}</th>
                <th className="text-right py-2.5 px-4 font-medium">{t('subscriptions.headcount.col_total')}</th>
                <th className="text-right py-2.5 px-4 font-medium">{t('subscriptions.headcount.col_users')}</th>
                <th className="text-right py-2.5 px-4 font-medium">{t('subscriptions.headcount.col_cards')}</th>
                <th className="text-right py-2.5 px-4 font-medium">{t('subscriptions.headcount.col_cpu')}</th>
                <th className="text-center py-2.5 px-4 font-medium">{t('subscriptions.headcount.col_type')}</th>
              </tr>
            </thead>
            <tbody>
              {vendorStats.map((v, i) => (
                <tr key={v.Normalized_Vendor} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="py-2.5 px-4 text-muted-foreground text-xs">{i + 1}</td>
                  <td className="py-2.5 px-4 font-medium">{v.Normalized_Vendor}</td>
                  <td className="py-2.5 px-4 text-xs text-muted-foreground">{v.Subscription_Category}</td>
                  <td className="py-2.5 px-4 text-right font-semibold">{fmt(v.Total_Amount)}</td>
                  <td className="py-2.5 px-4 text-right text-muted-foreground">{v.Users}</td>
                  <td className="py-2.5 px-4 text-right text-muted-foreground">{v.Cards}</td>
                  <td className="py-2.5 px-4 text-right font-bold text-primary">{fmtCPU(v.Total_Amount, v.Users)}</td>
                  <td className="py-2.5 px-4 text-center">
                    <div className="flex gap-1 justify-center">
                      {v.Is_AI_Tool && (
                        <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700">AI</Badge>
                      )}
                      {v.Is_AI_Native_Related && (
                        <Badge variant="secondary" className="text-xs bg-indigo-100 text-indigo-700">Native</Badge>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
